import { useState } from 'react';
import { 
  FaSearch, 
  FaFilter, 
  FaUser,
  FaEnvelope,
  FaPhone,
  FaCheckCircle,
  FaClock,
  FaTimes,
  FaEye,
  FaToggleOn,
  FaToggleOff,
  FaCrown,
  FaTag
} from 'react-icons/fa';
import useSWR from 'swr';
import Fetcher from '../../lib/Fetcher';
import HttpInterceptor from '../../lib/HttpInterceptor';
import { toast } from 'react-toastify';
import AdminFeatureModal from '../../Components/AdminFeatureModal';
import { formatName, toTitleCase } from '../../utils/dataTransformers';

function AllTrainers() {
  const [statusFilter, setStatusFilter] = useState('');
  const [serviceFilter, setServiceFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(null);
  const [showFeatureModal, setShowFeatureModal] = useState(false);
  const [selectedTrainer, setSelectedTrainer] = useState(null);

  const buildQuery = () => {
    const params = new URLSearchParams();
    if (statusFilter) params.append('status', statusFilter);
    if (serviceFilter) params.append('service', serviceFilter);
    params.append('page', page.toString());
    params.append('limit', '12');
    return `/api/admin/trainers?${params.toString()}`;
  };

  const { data, error, mutate } = useSWR(buildQuery(), Fetcher);
  const trainers = data?.data?.trainers || [];
  const pagination = data?.data?.pagination || { total: 0, pages: 1 };

  const handleStatusChange = async (trainerId, newStatus) => {
    setLoading(trainerId);
    try {
      await HttpInterceptor.patch(`/api/admin/trainers/${trainerId}/status`, {
        status: newStatus
      });
      toast.success(`Trainer status updated to ${newStatus}`);
      mutate();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update status');
    } finally {
      setLoading(null);
    }
  };

  const handleSubscriptionPlanChange = async (trainerId, newPlan, trainer) => {
    // If downgrading to free, directly call API
    if (newPlan === 'free') {
      setSubscriptionLoading(trainerId);
      try {
        await HttpInterceptor.patch(`/api/admin/trainers/${trainerId}/subscription`, {
          subscriptionPlan: 'free'
        });
        toast.success('Trainer subscription plan updated to free');
        mutate();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to update subscription plan');
      } finally {
        setSubscriptionLoading(null);
      }
    } else {
      // If upgrading to advance, show feature selection modal
      setSelectedTrainer(trainer);
      setShowFeatureModal(true);
    }
  };

  const handleSaveFeatures = async (features) => {
    if (!selectedTrainer) return;

    setSubscriptionLoading(selectedTrainer._id);
    try {
      await HttpInterceptor.patch(`/api/admin/trainers/${selectedTrainer._id}/subscription`, {
        subscriptionPlan: 'advance',
        advanceFeatures: features
      });
      toast.success('Trainer subscription plan and features updated successfully');
      setShowFeatureModal(false);
      setSelectedTrainer(null);
      mutate();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update subscription plan');
    } finally {
      setSubscriptionLoading(null);
    }
  };

  const handleCloseFeatureModal = () => {
    setShowFeatureModal(false);
    setSelectedTrainer(null);
  };

  const filteredTrainers = trainers.filter(trainer => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        trainer.name.toLowerCase().includes(query) ||
        trainer.email.toLowerCase().includes(query) ||
        trainer.mobile.includes(query) ||
        trainer.service.toLowerCase().includes(query)
      );
    }
    return true;
  });

  return (
    <div className="p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Business Partners</h1>
          <p className="text-gray-600">Manage all business partners and their accounts</p>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search trainers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
            </select>
            <select
              value={serviceFilter}
              onChange={(e) => {
                setServiceFilter(e.target.value);
                setPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">All Services</option>
              <option value="Yoga Trainer">Yoga Trainer</option>
              <option value="Dietitian">Dietitian</option>
              <option value="Gym Instructor">Gym Instructor</option>
              <option value="Zumba Instructor">Zumba Instructor</option>
              <option value="Reiki Healer">Reiki Healer</option>
              <option value="Astrologer">Astrologer</option>
              <option value="Numerologer">Numerologer</option>
              <option value="Ayurveda Practitioner">Ayurveda Practitioner</option>
              <option value="Others">Others</option>
            </select>
            <button
              onClick={() => {
                setStatusFilter('');
                setServiceFilter('');
                setSearchQuery('');
                setPage(1);
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Trainers Grid */}
        {error ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <p className="text-red-600">Failed to load trainers</p>
          </div>
        ) : filteredTrainers.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <FaUser className="text-6xl text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Trainers Found</h2>
            <p className="text-gray-600">Try adjusting your filters or search query.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              {filteredTrainers.map((trainer) => (
                <div key={trainer._id} className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                        <FaUser className="text-purple-600 text-xl" />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900">{formatName(trainer.name)}</h3>
                        <p className="text-sm text-gray-600">{toTitleCase(trainer.service)}</p>
                      </div>
                    </div>
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        trainer.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {trainer.status === 'active' ? (
                        <FaCheckCircle className="inline mr-1" />
                      ) : (
                        <FaClock className="inline mr-1" />
                      )}
                      {trainer.status}
                    </span>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <FaEnvelope className="mr-2 text-gray-400" />
                      <span className="truncate">{trainer.email}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <FaPhone className="mr-2 text-gray-400" />
                      <span>{trainer.mobile}</span>
                    </div>
                    {trainer.yearsOfExperience && (
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">Experience:</span> {trainer.yearsOfExperience} years
                      </div>
                    )}
                    <div className="flex items-center text-sm">
                      <FaTag className="mr-2 text-gray-400" />
                      <span className="font-medium text-gray-600">Plan:</span>
                      <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${
                        trainer.subscriptionPlan === 'advance'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {trainer.subscriptionPlan === 'advance' ? (
                          <>
                            <FaCrown className="inline mr-1" />
                            Advance
                          </>
                        ) : (
                          'Free'
                        )}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col space-y-2">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleStatusChange(trainer._id, trainer.status === 'active' ? 'inactive' : 'active')}
                        disabled={loading === trainer._id}
                        className={`flex-1 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center ${
                          trainer.status === 'active'
                            ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                            : 'bg-green-600 text-white hover:bg-green-700'
                        }`}
                      >
                        {loading === trainer._id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        ) : trainer.status === 'active' ? (
                          <>
                            <FaToggleOff className="mr-2" />
                            Deactivate
                          </>
                        ) : (
                          <>
                            <FaToggleOn className="mr-2" />
                            Activate
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => handleSubscriptionPlanChange(trainer._id, trainer.subscriptionPlan === 'advance' ? 'free' : 'advance', trainer)}
                        disabled={subscriptionLoading === trainer._id}
                        className={`px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center ${
                          trainer.subscriptionPlan === 'advance'
                            ? 'bg-gray-600 text-white hover:bg-gray-700'
                            : 'bg-purple-600 text-white hover:bg-purple-700'
                        }`}
                        title={trainer.subscriptionPlan === 'advance' ? 'Downgrade to Free' : 'Upgrade to Advance'}
                      >
                        {subscriptionLoading === trainer._id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        ) : trainer.subscriptionPlan === 'advance' ? (
                          <>
                            <FaTag className="mr-2" />
                            Free
                          </>
                        ) : (
                          <>
                            <FaCrown className="mr-2" />
                            Advance
                          </>
                        )}
                      </button>
                    </div>
                    {trainer.subscriptionPlan === 'advance' && (
                      <button
                        onClick={() => {
                          setSelectedTrainer(trainer);
                          setShowFeatureModal(true);
                        }}
                        className="w-full px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors text-sm flex items-center justify-center"
                        title="Edit Features"
                      >
                        <FaCrown className="mr-2" />
                        Manage Features
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex justify-center items-center space-x-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Previous
                </button>
                <span className="text-gray-600">
                  Page {page} of {pagination.pages}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page >= pagination.pages}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Feature Selection Modal */}
      <AdminFeatureModal
        isOpen={showFeatureModal}
        onClose={handleCloseFeatureModal}
        onSave={handleSaveFeatures}
        currentFeatures={
          selectedTrainer?.subscriptionPlan === 'advance' 
            ? selectedTrainer?.advanceFeatures || null
            : null // When upgrading to advance, pass null to enable all features by default
        }
      />
    </div>
  );
}

export default AllTrainers;

